import { useEffect, useMemo, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { fmtSize } from "@/lib/files";
import { FileThumb } from "@/components/FileThumb";
import { FileOrderChips } from "@/components/FileOrderChips";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";

function ReuseRow({ f, linked, checked, onToggle, onPreview }) {
  const id = `reuse-check-${f.id}`;
  return (
    <li className={`flex items-center gap-3 p-2.5 rounded-md border transition-colors ${checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`reuse-row-${f.id}`}>
      <Checkbox id={id} checked={checked} disabled={linked} onCheckedChange={onToggle} data-testid={`reuse-checkbox-${f.id}`} />
      <FileThumb att={f} className="w-10 h-10" />
      <label htmlFor={id} className={`flex-1 min-w-0 ${linked ? "cursor-default" : "cursor-pointer"}`}>
        <div className="text-sm font-medium text-slate-900 truncate" title={f.original_name}>{f.original_name}{f.version > 1 ? ` (v${f.version})` : ""}</div>
        <div className="text-xs text-slate-500">{fmtSize(f.size)} · {f.ext.toUpperCase()} · {fmtDateTime(f.first_uploaded_at)}{linked ? " · bereits in diesem Auftrag" : ""}</div>
      </label>
      <div className="hidden sm:block max-w-[40%]"><FileOrderChips orders={f.orders} testPrefix={`reuse-${f.id}`} /></div>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onPreview(f)} title="Vorschau" data-testid={`reuse-preview-${f.id}`}><Eye className="w-4 h-4" /></Button>
    </li>
  );
}

// „Vorhandene Kundendateien verwenden“: verknüpft Dateien des Kunden mit dem Auftrag (keine Kopie)
export function ReuseCustomerFilesDialog({ open, onClose, orderId, kundeId, onLinked }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || !kundeId) return;
    setSel([]); setQ(""); setList(null);
    api.get(`/kunden/${kundeId}/dateien`).then((r) => setList(r.data)).catch(() => { setList([]); toast.error("Kundendateien konnten nicht geladen werden."); });
  }, [open, kundeId]);

  const isLinked = (f) => f.orders.some((o) => o.order_id === orderId);
  const shown = useMemo(() => (list || []).filter((f) => f.original_name.toLowerCase().includes(q.trim().toLowerCase())), [list, q]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/auftraege/${orderId}/attachments/verknuepfen`, { attachment_ids: sel });
      const n = r.data.linked.length;
      toast.success(n === 1 ? "1 Datei mit dem Auftrag verknüpft." : `${n} Dateien mit dem Auftrag verknüpft.`);
      onLinked?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verknüpfen fehlgeschlagen."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="reuse-files-dialog">
        <DialogHeader>
          <DialogTitle>Vorhandene Kundendateien verwenden</DialogTitle>
          <DialogDescription>Ausgewählte Dateien werden mit diesem Auftrag verknüpft – es wird keine Kopie angelegt.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Dateiname suchen…" className="pl-9" aria-label="Dateiname suchen" data-testid="reuse-search-input" />
        </div>
        <div className="max-h-[50vh] overflow-y-auto pr-1">
          {list === null ? (
            <div className="py-8 flex justify-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Lädt…</div>
          ) : shown.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="reuse-empty">{list.length ? "Keine passende Datei gefunden." : "Für diesen Kunden sind noch keine Dateien vorhanden."}</p>
          ) : (
            <ul className="space-y-2">
              {shown.map((f) => <ReuseRow key={f.id} f={f} linked={isLinked(f)} checked={sel.includes(f.id)} onToggle={() => toggle(f.id)} onPreview={setPreview} />)}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reuse-cancel-button">Abbrechen</Button>
          <Button onClick={submit} disabled={!sel.length || busy} data-testid="reuse-submit-button">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Verknüpfen{sel.length ? ` (${sel.length})` : ""}
          </Button>
        </DialogFooter>
        <FilePreviewDialog att={preview} onClose={() => setPreview(null)} />
      </DialogContent>
    </Dialog>
  );
}
